import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Typography,
    Button,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Grid,
    Dialog,
    IconButton,
    FormControlLabel,
    Switch,
    Alert,
    Chip,
    Collapse,
    CircularProgress
} from '@mui/material';
import {
    Add as AddIcon,
    RemoveCircleOutline,
    LocalHospital as ClinicIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { patientAPI, doctorAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import ConfirmationDialog from './ConfirmationDialog';
import PatientFormDialog from '../patients/PatientForm';
import { toast } from 'react-toastify';
import OptimizedSearchableAutocomplete from '../../components/common/OptimizedSearchableAutocomplete';
import {
    searchPatients,
    searchDoctors,
    searchRadiologists,
    searchBranches,
    searchTechnicians,
    searchScans
} from '../../services/optimizedSearchService';
import PriceSummary from '../../components/appointments/priceSummery';
import DiscountOptions from '../../components/appointments/discoundOptions';

// ─── Helper: safely extract _id string from object or string ─────────────────
const getId = (field) =>
    field ? (typeof field === 'object' ? field._id : String(field)) : null;

function AppointmentForm({
    open,
    onClose,
    onSubmit,
    initialData,
    patients,
    doctors,
    radiologists,
    branches,
    scans,
    technicians,
    setPatients
}) {
    const { t } = useTranslation();
    const { user, isSuperAdmin } = useAuth();
    const today = new Date();
    const prevOpenRef = useRef(false);

    // ─── Default form state ────────────────────────────────────────────────
    const defaultFormState = {
        radiologistId: null,
        patientId: null,
        scans: [{ scan: '', quantity: 1, items: [] }],
        referredBy: null,
        clinicId: null,
        branch: isSuperAdmin ? '' : (user?.branch?._id || user?.branch || ''),
        scheduledDate: today,
        scheduledTime: null,
        notes: '',
        priority: 'routine',
        makeHugeSale: false,
        customPrice: '',
        completionFee: '',
        discountAmount: '',
        isCompleted: false,
        radiologyTechnician: null,
        isPaid: false,
    };

    const [form, setForm] = useState(defaultFormState);
    const [errors, setErrors] = useState({});
    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingSubmission, setPendingSubmission] = useState(null);
    const [showAddPatient, setShowAddPatient] = useState(false);
    const [newlyCreatedPatientId, setNewlyCreatedPatientId] = useState(null);
    const [initialPrice, setInitialPrice] = useState(null);

    // Selected display objects
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [selectedScans, setSelectedScans] = useState({});

    // ─── Doctor clinics state ──────────────────────────────────────────────
    const [doctorClinics, setDoctorClinics] = useState([]);
    const [selectedClinic, setSelectedClinic] = useState(null);
    const [showClinicSelector, setShowClinicSelector] = useState(false);
    // 🆕 NEW: Loading state for fetching full doctor data
    const [loadingDoctorClinics, setLoadingDoctorClinics] = useState(false);

    // ─── Memoized initial options ──────────────────────────────────────────
    const patientInitialOptions = useMemo(() => ([
        { _id: 'add_patient', name: 'Add New Patient' },
        ...patients.slice(0, 20)
    ]), [patients]);

    const doctorInitialOptions = useMemo(() => doctors.slice(0, 20), [doctors]);
    const branchInitialOptions = useMemo(() => branches, [branches]);
    const radiologistInitialOptions = useMemo(() => radiologists, [radiologists]);
    const technicianInitialOptions = useMemo(() => technicians, [technicians]);
    const scanInitialOptions = useMemo(() => scans.slice(0, 20), [scans]);

    // =========================================================================
    // 🆕 NEW: Helper to fetch full doctor data (with clinics)
    // =========================================================================
    const fetchFullDoctor = useCallback(async (doctorId) => {
        if (!doctorId) return null;
        try {
            setLoadingDoctorClinics(true);
            const response = await doctorAPI.getById(doctorId);
            return response.data?.data || response.data || null;
        } catch (err) {
            console.error('❌ Error fetching full doctor data:', err);
            return null;
        } finally {
            setLoadingDoctorClinics(false);
        }
    }, []);

    // =========================================================================
    // 🆕 NEW: Helper to resolve doctor with clinics from all sources
    // =========================================================================
    const resolveDoctorWithClinics = useCallback(async (referredBy, existingClinicId) => {
        // referredBy can be a populated object or just an ID
        const doctorId = getId(referredBy);
        if (!doctorId) return { doctor: null, clinics: [], clinic: null };

        // Build initial doctor object from what we have
        let doctorObj = typeof referredBy === 'object' ? referredBy : null;

        // Try to find the full doctor from the doctors prop (which includes clinics)
        const fromProps = doctors.find(d => (d._id || d.id) === doctorId);

        // Determine if we already have clinics data
        let clinics = [];

        if (fromProps?.clinics?.length > 0) {
            // Props doctor has clinics — use it
            doctorObj = fromProps;
            clinics = fromProps.clinics;
        } else if (doctorObj?.clinics?.length > 0) {
            // Populated object from API has clinics
            clinics = doctorObj.clinics;
        } else {
            // Neither has clinics — fetch full doctor from API
            const fullDoctor = await fetchFullDoctor(doctorId);
            if (fullDoctor) {
                // Merge: keep display fields from populated object, add clinics from full fetch
                doctorObj = {
                    ...doctorObj,
                    ...fullDoctor,
                    // Keep the populated specialization name if it was an object
                    specialization: doctorObj?.specialization || fullDoctor.specialization
                };
                clinics = fullDoctor.clinics || [];
            }
        }

        // Resolve selected clinic
        let resolvedClinic = null;
        if (clinics.length > 0 && existingClinicId) {
            resolvedClinic = clinics.find(
                c => getId(c._id) === existingClinicId
            ) || null;
        }

        return { doctor: doctorObj, clinics, clinic: resolvedClinic };
    }, [doctors, fetchFullDoctor]);

    // =========================================================================
    // INITIALIZATION EFFECT — runs only when dialog opens
    // =========================================================================
    useEffect(() => {
        const justOpened = open && !prevOpenRef.current;
        prevOpenRef.current = open;
        if (!justOpened) return;

        if (initialData) {
            // Use an async IIFE so we can await the doctor fetch
            (async () => {
                const initialDate = initialData.scheduledAt
                    ? new Date(initialData.scheduledAt)
                    : null;

                // ── Patient ──────────────────────────────────────────────────
                const patientId = getId(initialData.patientId);
                const patientObj = typeof initialData.patientId === 'object'
                    ? initialData.patientId
                    : patients.find(p => p._id === patientId) || null;
                setSelectedPatient(patientObj);

                // ── Initial price snapshot ───────────────────────────────────
                setInitialPrice(
                    initialData.basePrice != null
                        ? Number(initialData.basePrice)
                        : (initialData.price != null ? Number(initialData.price) : null)
                );

                // ── Scans ────────────────────────────────────────────────────
                const initialSelectedScans = {};
                const formScans = (initialData.scans || []).map(s => {
                    const scanId = getId(s.scan);
                    const scanFromProps = scans.find(scan => scan._id === scanId);
                    const scanFromInitialData = typeof s.scan === 'object' ? s.scan : null;
                    const scanObj = scanFromProps || scanFromInitialData;

                    if (scanObj && scanId) {
                        initialSelectedScans[scanId] = scanObj;
                    }

                    return {
                        scan: scanId,
                        quantity: s.quantity || 1,
                        items: s.items
                            ? s.items.map(item => ({ ...item }))
                            : (scanObj?.items?.map(item => ({ ...item })) || [])
                    };
                });
                setSelectedScans(initialSelectedScans);

                // ── 🆕 FIXED: Doctor + clinic resolution ─────────────────────
                const existingClinicId = getId(initialData.clinicId) || null;
                const { doctor: resolvedDoctor, clinics, clinic: resolvedClinic } =
                    await resolveDoctorWithClinics(initialData.referredBy, existingClinicId);

                // Populate clinic selector state
                setDoctorClinics(clinics);
                setSelectedClinic(resolvedClinic);
                setShowClinicSelector(clinics.length > 0);

                // ── Set form ─────────────────────────────────────────────────
                setForm({
                    patientId,
                    branch: getId(initialData.branch),
                    referredBy: resolvedDoctor,
                    clinicId: existingClinicId,
                    radiologistId: getId(initialData.radiologistId),
                    radiologyTechnician: getId(initialData.radiologyTechnician),
                    scheduledDate: initialDate,
                    scheduledTime: initialDate,
                    notes: initialData.notes || '',
                    priority: initialData.priority || 'routine',
                    scans: formScans.length > 0
                        ? formScans
                        : [{ scan: '', quantity: 1, items: [] }],
                    completionFee: initialData.completionFee != null
                        ? String(initialData.completionFee)
                        : '',
                    customPrice: initialData.customPrice != null
                        ? String(initialData.customPrice)
                        : '',
                    discountAmount: initialData.discountAmount != null
                        ? String(initialData.discountAmount)
                        : '',
                    makeHugeSale: !!(
                        initialData.makeHugeSale && initialData.customPrice != null
                    ),
                    isCompleted: initialData.isCompleted || false,
                    isPaid: !!initialData.isPaid,
                });
            })();
        } else {
            // ── Fresh form ───────────────────────────────────────────────────
            setSelectedPatient(null);
            setSelectedScans({});
            setInitialPrice(null);
            setDoctorClinics([]);
            setSelectedClinic(null);
            setShowClinicSelector(false);
            setForm(defaultFormState);
        }

        setErrors({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialData]);

    // =========================================================================
    // NEWLY CREATED PATIENT EFFECT
    // =========================================================================
    useEffect(() => {
        if (newlyCreatedPatientId) {
            setForm(prev => ({ ...prev, patientId: newlyCreatedPatientId }));
            setNewlyCreatedPatientId(null);
        }
    }, [newlyCreatedPatientId]);

    // =========================================================================
    // PRICE CALCULATION
    // =========================================================================
    const calculatedBasePrice = useMemo(() =>
        form.scans.reduce((acc, currentScan) => {
            const scanDetails =
                selectedScans[currentScan.scan] ||
                scans.find(s => s._id === currentScan.scan);

            if (!scanDetails || !form.branch) return acc;

            const branchPricing = scanDetails.branchPricing?.find(bp =>
                bp.branch?._id === form.branch || bp.branch === form.branch
            );

            const unitPrice = branchPricing?.price ?? scanDetails.price ?? 0;
            return acc + (unitPrice * (currentScan.quantity || 1));
        }, 0),
        [form.scans, form.branch, scans, selectedScans]
    );

    const basePrice = useMemo(() => {
        if (calculatedBasePrice > 0) return calculatedBasePrice;
        if (initialPrice != null && initialData) return initialPrice;
        return 0;
    }, [calculatedBasePrice, initialPrice, initialData]);

    const parseNumericValue = useCallback((value, defaultValue = 0) => {
        if (value === '' || value === null || value === undefined) return defaultValue;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? defaultValue : parsed;
    }, []);

    const parsedCustomPrice = useMemo(() => {
        if (
            form.customPrice === '' ||
            form.customPrice === null ||
            form.customPrice === undefined
        ) return null;
        const parsed = parseFloat(form.customPrice);
        return isNaN(parsed) ? null : parsed;
    }, [form.customPrice]);

    const parsedCompletionFee = useMemo(
        () => parseNumericValue(form.completionFee, 0),
        [form.completionFee, parseNumericValue]
    );

    const parsedDiscountAmount = useMemo(
        () => parseNumericValue(form.discountAmount, 0),
        [form.discountAmount, parseNumericValue]
    );

    const finalPrice = useMemo(() => {
        if (form.makeHugeSale && parsedCustomPrice != null) {
            return Math.max(0, parsedCustomPrice + parsedCompletionFee);
        }
        return Math.max(
            0,
            basePrice - parsedDiscountAmount + parsedCompletionFee
        );
    }, [
        basePrice,
        parsedDiscountAmount,
        parsedCompletionFee,
        form.makeHugeSale,
        parsedCustomPrice
    ]);

    const hasHugeSalePrivilege =
        user?.isSuperAdmin ||
        user?.privileges?.some(
            p => p.module === 'appointments' && p.operation === 'makeHugeSale'
        );

    // =========================================================================
    // AUTOCOMPLETE HELPERS
    // =========================================================================
    const getStandardResults = useCallback(
        (response) => response.data?.data || [],
        []
    );
    const getStandardPagination = useCallback((response) => {
        const pag = response.data?.pagination;
        return { hasMore: pag ? pag.page < pag.pages : false };
    }, []);

    // ─── Patient ───────────────────────────────────────────────────────────
    const handlePatientChange = useCallback((event, newValue) => {
        if (newValue?._id === 'add_patient') {
            setShowAddPatient(true);
        } else {
            setSelectedPatient(newValue);
            setForm(prev => ({ ...prev, patientId: newValue?._id || '' }));
        }
    }, []);

    const getPatientOptionLabel = useCallback((option) => {
        if (!option) return '';
        return option._id === 'add_patient'
            ? option.name
            : `${option.name || ''} - ${option.phoneNumber || ''}`;
    }, []);

    const renderPatientOption = useCallback((props, option, { highlightText, inputValue }) => (
        <Box component="li" {...props} key={option._id}>
            {option._id === 'add_patient' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <AddIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography color="primary.main">{t('patients.addPatient')}</Typography>
                </Box>
            ) : (
                <Box sx={{ width: '100%', overflow: 'hidden' }}>
                    <Typography noWrap>
                        {highlightText(option.name, inputValue)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                        {highlightText(option.phoneNumber, inputValue)}
                    </Typography>
                </Box>
            )}
        </Box>
    ), [t]);

    // ─── Doctor ────────────────────────────────────────────────────────────
    // 🆕 FIXED: Also fetch full doctor data when selecting from dropdown
    const handleDoctorChange = useCallback(async (event, newValue) => {
        if (!newValue) {
            setDoctorClinics([]);
            setSelectedClinic(null);
            setShowClinicSelector(false);
            setForm(prev => ({
                ...prev,
                referredBy: null,
                clinicId: null,
            }));
            return;
        }

        // Check if the selected doctor already has clinics
        let clinics = newValue.clinics || [];

        if (clinics.length === 0) {
            // Fetch full doctor data to get clinics
            const fullDoctor = await fetchFullDoctor(newValue._id || newValue.id);
            if (fullDoctor?.clinics?.length > 0) {
                clinics = fullDoctor.clinics;
                // Merge clinics into the doctor object
                newValue = { ...newValue, clinics };
            }
        }

        setDoctorClinics(clinics);
        setSelectedClinic(null);
        setShowClinicSelector(clinics.length > 0);

        setForm(prev => ({
            ...prev,
            referredBy: newValue,
            clinicId: null,
        }));
    }, [fetchFullDoctor]);

    const getDoctorOptionLabel = useCallback((option) => {
        if (!option) return '';
        return `${option.name || ''} - ${option?.specialization?.name || option?.specialization || 'N/A'}`;
    }, []);

    const renderDoctorOption = useCallback(
        (props, option, { highlightText, inputValue }) => (
            <Box component="li" {...props} key={option._id}>
                <Box sx={{ width: '100%', overflow: 'hidden' }}>
                    <Typography variant="body1" noWrap>
                        {highlightText(option.name, inputValue)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                        {highlightText(
                            option?.specialization?.name ||
                            option?.specialization ||
                            'General',
                            inputValue
                        )}
                    </Typography>
                </Box>
            </Box>
        ),
        []
    );

    // ─── Clinic selection (from doctor.clinics[]) ──────────────────────────
    const handleClinicSelect = useCallback((clinic) => {
        const isAlreadySelected =
            selectedClinic && getId(selectedClinic._id) === getId(clinic._id);

        if (isAlreadySelected) {
            setSelectedClinic(null);
            setForm(prev => ({ ...prev, clinicId: null }));
        } else {
            setSelectedClinic(clinic);
            setForm(prev => ({ ...prev, clinicId: getId(clinic._id) }));
        }
    }, [selectedClinic]);

    // ─── Branch ────────────────────────────────────────────────────────────
    const handleBranchChange = useCallback((e, nv) => {
        setForm(p => ({
            ...p,
            branch: nv?._id || '',
            scans: [{ scan: '', quantity: 1, items: [] }]
        }));
        setSelectedScans({});
    }, []);

    // ─── Radiologist / Technician ──────────────────────────────────────────
    const handleRadiologistChange = useCallback(
        (e, nv) => setForm(p => ({ ...p, radiologistId: nv?._id || null })),
        []
    );

    const handleTechnicianChange = useCallback(
        (e, nv) => setForm(p => ({ ...p, radiologyTechnician: nv?._id || null })),
        []
    );

    const getSimpleOptionLabel = useCallback((o) => {
        if (!o) return '';
        return o.name || o.username || '';
    }, []);

    // ─── Pricing handlers ──────────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const MAX_DISCOUNT_PERCENTAGE = 20; // Move this to top of component if not already

    const handleDiscountChange = useCallback((value) => {
        if (value === '' || value === null || value === undefined) {
            setForm(prev => ({ ...prev, discountAmount: '' }));
            setErrors(prev => ({ ...prev, discountAmount: undefined }));
            return;
        }

        const numericValue = parseFloat(value);

        // ═══════════════════════════════════════════════════════════════
        // 🔒 Real-time discount limit for non-superAdmin
        // ═══════════════════════════════════════════════════════════════
        if (!isSuperAdmin && !isNaN(numericValue) && basePrice > 0) {
            const discountPercentage = (numericValue / basePrice) * 100;
            if (discountPercentage > MAX_DISCOUNT_PERCENTAGE) {
                const maxAllowed = (basePrice * MAX_DISCOUNT_PERCENTAGE / 100);
                setErrors(prev => ({
                    ...prev,
                    discountAmount:
                        t('appointments.discountExceedsLimit') ||
                        `Max discount: ${MAX_DISCOUNT_PERCENTAGE}% (${maxAllowed.toFixed(2)})`
                }));
                // Still set the value so user sees what they typed
                setForm(prev => ({ ...prev, discountAmount: value }));
                return;
            }
        }

        // Clear error if within limit
        setErrors(prev => ({ ...prev, discountAmount: undefined }));
        setForm(prev => ({ ...prev, discountAmount: value }));
    }, [isSuperAdmin, basePrice, t]);
    const handleCompletionFeeChange = useCallback((value) => {
        setForm(prev => ({ ...prev, completionFee: value }));
    }, []);

    const handleCustomPriceChange = useCallback((value) => {
        const hasValue = value !== '' && value !== null && value !== undefined;
        setForm(prev => ({
            ...prev,
            customPrice: value,
            makeHugeSale: hasValue
        }));
    }, []);

    // ─── Scan handlers ─────────────────────────────────────────────────────
    const handleScanChange = useCallback((index, field, value) => {
        if (field === 'scan') {
            if (value && value._id) {
                setSelectedScans(prev => ({ ...prev, [value._id]: value }));
            }
            setForm(prev => {
                const newScans = [...prev.scans];
                newScans[index] = {
                    scan: value?._id || '',
                    quantity: 1,
                    items: value?.items?.map(item => ({ ...item })) || []
                };
                return { ...prev, scans: newScans };
            });
        } else {
            setForm(prev => {
                const newScans = [...prev.scans];
                newScans[index] = { ...newScans[index], [field]: value };
                return { ...prev, scans: newScans };
            });
        }
    }, []);

    const handleScanItemChange = useCallback((scanIndex, itemIndex, newQuantity) => {
        setForm(prev => {
            const newScans = prev.scans.map(s => ({
                ...s,
                items: s.items.map(i => ({ ...i }))
            }));
            if (newScans[scanIndex]?.items?.[itemIndex]) {
                newScans[scanIndex].items[itemIndex].quantity =
                    newQuantity === ''
                        ? ''
                        : Math.max(0, parseInt(newQuantity, 10) || 0);
            }
            return { ...prev, scans: newScans };
        });
    }, []);

    const addScan = useCallback(() =>
        setForm(prev => ({
            ...prev,
            scans: [...prev.scans, { scan: '', quantity: 1, items: [] }]
        })),
        []
    );

    const removeScan = useCallback((index) => {
        setForm(prev => {
            if (prev.scans.length <= 1) return prev;
            const scanToRemove = prev.scans[index];
            if (scanToRemove.scan) {
                setSelectedScans(prevSelected => {
                    const next = { ...prevSelected };
                    delete next[scanToRemove.scan];
                    return next;
                });
            }
            return { ...prev, scans: prev.scans.filter((_, i) => i !== index) };
        });
    }, []);

    const getScanObject = useCallback((scanId) => {
        if (!scanId) return null;
        return selectedScans[scanId] || scans.find(s => s._id === scanId) || null;
    }, [selectedScans, scans]);

    const searchScansWithBranch = useCallback(
        (searchTerm, options) =>
            searchScans(searchTerm, { ...options, branchId: form.branch }),
        [form.branch]
    );
    const validateForm = useCallback(() => {
        const newErrors = {};

        if (!form.patientId)
            newErrors.patientId = t('appointments.patientRequired');
        if (!form.scheduledDate)
            newErrors.scheduledDate = t('appointments.dateRequired');
        if (!form.scheduledTime)
            newErrors.scheduledTime = t('appointments.timeRequired') || 'Time is required';
        if (!form.branch)
            newErrors.branch = t('appointments.branchRequired');
        if (!form.scans || form.scans.every(s => !s.scan))
            newErrors.scans = t('appointments.scanRequired') || 'At least one scan must be selected';

        if (form.makeHugeSale) {
            if (!hasHugeSalePrivilege) {
                newErrors.makeHugeSale = t('appointments.noHugeSalePrivilege') ||
                    'You do not have permission to make huge sales';
            }
            if (parsedCustomPrice === null || parsedCustomPrice < 0) {
                newErrors.customPrice = t('appointments.customPriceRequired') ||
                    'Custom price is required for huge sales';
            }
        }

        // ═══════════════════════════════════════════════════════════════
        // 🔒 Discount limit: non-superAdmin users cannot exceed 20%
        // ═══════════════════════════════════════════════════════════════
        if (!form.makeHugeSale && parsedDiscountAmount > 0 && basePrice > 0) {
            const discountPercentage = (parsedDiscountAmount / basePrice) * 100;
            if (!isSuperAdmin && discountPercentage > MAX_DISCOUNT_PERCENTAGE) {
                newErrors.discountAmount =
                    t('appointments.discountExceedsLimit') ||
                    `Discount cannot exceed ${MAX_DISCOUNT_PERCENTAGE}% for non-admin users (max: ${(basePrice * MAX_DISCOUNT_PERCENTAGE / 100).toFixed(2)})`;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [form, t, hasHugeSalePrivilege, parsedCustomPrice, parsedDiscountAmount, basePrice, isSuperAdmin]);

    // =========================================================================
    // SUBMISSION
    // =========================================================================
    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const { scheduledDate, scheduledTime } = form;

        const combinedDateTime = new Date(
            scheduledDate.getFullYear(),
            scheduledDate.getMonth(),
            scheduledDate.getDate(),
            scheduledTime.getHours(),
            scheduledTime.getMinutes()
        );

        const submissionCustomPrice =
            parsedCustomPrice != null ? Math.max(0, parsedCustomPrice) : null;
        const submissionCompletionFee = Math.max(0, parsedCompletionFee);
        const submissionDiscountAmount = Math.max(0, parsedDiscountAmount);

        const submissionData = {
            patientId: form.patientId,
            branch: form.branch,
            radiologistId: form.radiologistId || null,
            radiologyTechnician: form.radiologyTechnician || null,
            referredBy: form.referredBy?._id || form.referredBy || null,
            clinicId: form.clinicId || null,
            scheduledAt: combinedDateTime.toISOString(),
            notes: form.notes || '',
            priority: form.priority || 'routine',
            makeHugeSale: !!(form.makeHugeSale && submissionCustomPrice != null),
            customPrice: submissionCustomPrice,
            completionFee: submissionCompletionFee,
            discountAmount: submissionDiscountAmount,
            isCompleted: !!form.isCompleted,
            isPaid: !!form.isPaid,
            scans: form.scans
                .filter(s => s.scan)
                .map(s => ({
                    scan: s.scan,
                    quantity: Math.max(1, Number(s.quantity) || 1),
                    items: (s.items || []).map(item => ({
                        item: item.item,
                        quantity: Math.max(0, Number(item.quantity) || 0)
                    }))
                })),
        };

        const displayData = {
            patient: selectedPatient,
            branchObj: branches.find(b => b._id === form.branch),
            radiologist: radiologists.find(r => r._id === form.radiologistId),
            technician: technicians.find(tech => tech._id === form.radiologyTechnician),
            scheduledDate,
            scheduledTime,
            doctor: form.referredBy,
            clinic: selectedClinic,
        };

        setPendingSubmission({
            ...submissionData,
            ...displayData,
            finalPrice,
            basePrice
        });
        setShowConfirm(true);
    }, [
        form,
        validateForm,
        parsedCustomPrice,
        parsedCompletionFee,
        parsedDiscountAmount,
        selectedPatient,
        selectedClinic,
        branches,
        radiologists,
        technicians,
        finalPrice,
        basePrice
    ]);

    const handleConfirm = useCallback(() => {
        if (!pendingSubmission) return;

        const {
            finalPrice: _fp,
            basePrice: _bp,
            patient: _pat,
            branchObj: _branchObj,
            radiologist: _rad,
            technician: _tech,
            scheduledDate: _sd,
            scheduledTime: _st,
            doctor: _doc,
            clinic: _clinic,
            ...apiPayload
        } = pendingSubmission;

        delete apiPayload.representative;

        if (initialData?._id) {
            apiPayload._id = initialData._id;
        }

        console.log(
            '--- [AppointmentForm] Submitting payload:',
            JSON.stringify(apiPayload, null, 2)
        );

        onSubmit(apiPayload);
        setShowConfirm(false);
        setPendingSubmission(null);
    }, [pendingSubmission, initialData, onSubmit]);

    // =========================================================================
    // ADD PATIENT HANDLER
    // =========================================================================
    const handlePatientFormSubmit = useCallback(async (newPatientData) => {
        try {
            const payload = {
                ...newPatientData,
                doctorReferred: newPatientData.doctorReferred?._id
            };
            const createdPatientRes = await patientAPI.create(payload);
            const newPatient = createdPatientRes.data.data;

            setPatients(prev => [newPatient, ...prev]);
            setSelectedPatient(newPatient);
            setForm(prev => ({ ...prev, patientId: newPatient._id }));
            setShowAddPatient(false);
            toast.success(t('patients.createSuccess'));
        } catch (err) {
            console.error('❌ Error creating patient:', err);
            toast.error(err.response?.data?.message || t('patients.createError'));
        }
    }, [setPatients, t]);

    // =========================================================================
    // RENDER
    // =========================================================================
    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {initialData
                        ? t('appointments.editTitle')
                        : t('appointments.addTitle')}
                </DialogTitle>

                <DialogContent>
                    <form onSubmit={handleSubmit} id="appointment-form">
                        <Grid container spacing={2} sx={{ mt: 1 }}>

                            {/* ── Patient ───────────────────────────────── */}
                            <Grid item xs={12} md={6}>
                                <OptimizedSearchableAutocomplete
                                    key={selectedPatient?._id || 'no-patient'}
                                    label={t('appointments.patient')}
                                    searchFunction={searchPatients}
                                    value={selectedPatient}
                                    initialOptions={patientInitialOptions}
                                    onChange={handlePatientChange}
                                    getOptionLabel={getPatientOptionLabel}
                                    renderOption={renderPatientOption}
                                    error={!!errors.patientId}
                                    helperText={errors.patientId}
                                    getResults={getStandardResults}
                                    getPagination={getStandardPagination}
                                />
                            </Grid>

                            {/* ── Doctor ────────────────────────────────── */}
                            <Grid item xs={12} md={6}>
                                <OptimizedSearchableAutocomplete
                                    label={`${t('appointments.doctor')} (${t('common.optional')})`}
                                    searchFunction={searchDoctors}
                                    value={form.referredBy}
                                    onChange={handleDoctorChange}
                                    initialOptions={doctorInitialOptions}
                                    getOptionLabel={getDoctorOptionLabel}
                                    renderOption={renderDoctorOption}
                                    getResults={getStandardResults}
                                    getPagination={getStandardPagination}
                                />
                            </Grid>

                            {/* ── Clinic Selector (shown when doctor has clinics) ── */}
                            <Grid item xs={12}>
                                <Collapse in={showClinicSelector}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            border: '1px solid',
                                            borderColor: selectedClinic ? 'primary.main' : 'divider',
                                            borderRadius: 2,
                                            transition: 'border-color 0.3s ease',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1.5
                                            }}
                                        >
                                            <ClinicIcon
                                                fontSize="small"
                                                color="primary"
                                            />
                                            <Typography
                                                variant="subtitle2"
                                                color="text.secondary"
                                            >
                                                {t('appointments.selectClinic') ||
                                                    'Select Clinic (Optional)'}
                                            </Typography>

                                            {/* 🆕 Loading indicator while fetching clinics */}
                                            {loadingDoctorClinics && (
                                                <CircularProgress size={16} sx={{ ml: 1 }} />
                                            )}
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 1
                                            }}
                                        >
                                            {doctorClinics.map((clinic) => {
                                                const clinicId = getId(clinic._id);
                                                const isSelected =
                                                    selectedClinic &&
                                                    getId(selectedClinic._id) === clinicId;

                                                // 🆕 Show representative name on chip if available
                                                const repName = clinic.representative?.name ||
                                                    clinic.representative?.username || null;

                                                return (
                                                    <Chip
                                                        key={clinicId}
                                                        label={
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                                <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                                                                    {clinic.name ||
                                                                        clinic.location ||
                                                                        `Clinic ${clinicId?.slice(-4)}`}
                                                                </Typography>
                                                                {repName && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                                                        {repName}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        }
                                                        onClick={() =>
                                                            handleClinicSelect(clinic)
                                                        }
                                                        color={
                                                            isSelected
                                                                ? 'primary'
                                                                : 'default'
                                                        }
                                                        variant={
                                                            isSelected
                                                                ? 'filled'
                                                                : 'outlined'
                                                        }
                                                        icon={<ClinicIcon />}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            height: 'auto',
                                                            py: 0.5,
                                                            '& .MuiChip-label': {
                                                                display: 'block',
                                                                whiteSpace: 'normal',
                                                            }
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>

                                        {selectedClinic && (
                                            <Alert
                                                severity="info"
                                                sx={{ mt: 1.5 }}
                                                icon={false}
                                            >
                                                <Typography variant="caption">
                                                    {t('appointments.clinicRepresentativeNote') ||
                                                        'The representative will be automatically resolved from this clinic.'}
                                                </Typography>
                                            </Alert>
                                        )}
                                    </Box>
                                </Collapse>
                            </Grid>

                            {/* ── Branch ───────────────────────────────── */}
                            <Grid item xs={12} md={6}>
                                <OptimizedSearchableAutocomplete
                                    label={t('appointments.branchLabel')}
                                    searchFunction={searchBranches}
                                    value={
                                        branches.find(b => b._id === form.branch) ||
                                        null
                                    }
                                    onChange={handleBranchChange}
                                    getOptionLabel={getSimpleOptionLabel}
                                    initialOptions={branchInitialOptions}
                                    disabled={!isSuperAdmin}
                                    error={!!errors.branch}
                                    helperText={errors.branch}
                                    getResults={getStandardResults}
                                    getPagination={getStandardPagination}
                                />
                            </Grid>

                            {/* ── Radiologist ───────────────────────────── */}
                            <Grid item xs={12} md={6}>
                                <OptimizedSearchableAutocomplete
                                    label={`${t('appointments.radiologist')} (${t('common.optional')})`}
                                    searchFunction={searchRadiologists}
                                    value={
                                        radiologists.find(
                                            r => r._id === form.radiologistId
                                        ) || null
                                    }
                                    onChange={handleRadiologistChange}
                                    getOptionLabel={getSimpleOptionLabel}
                                    initialOptions={radiologistInitialOptions}
                                    getResults={getStandardResults}
                                    getPagination={getStandardPagination}
                                />
                            </Grid>

                            {/* ── Technician ────────────────────────────── */}
                            <Grid item xs={12} md={6}>
                                <OptimizedSearchableAutocomplete
                                    label={`${t('appointments.technician')} (${t('common.optional')})`}
                                    searchFunction={searchTechnicians}
                                    value={
                                        technicians.find(
                                            tech => tech._id === form.radiologyTechnician
                                        ) || null
                                    }
                                    onChange={handleTechnicianChange}
                                    getOptionLabel={getSimpleOptionLabel}
                                    initialOptions={technicianInitialOptions}
                                    getResults={getStandardResults}
                                    getPagination={getStandardPagination}
                                />
                            </Grid>

                            {/* ── Date ─────────────────────────────────── */}
                            <Grid item xs={12} md={6}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label={t('appointments.date')}
                                        value={form.scheduledDate}
                                        onChange={v =>
                                            setForm(p => ({ ...p, scheduledDate: v }))
                                        }
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: !!errors.scheduledDate,
                                                helperText: errors.scheduledDate
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            {/* ── Time ─────────────────────────────────── */}
                            <Grid item xs={12} md={6}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <TimePicker
                                        label={t('appointments.time')}
                                        value={form.scheduledTime}
                                        onChange={v =>
                                            setForm(p => ({ ...p, scheduledTime: v }))
                                        }
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: !!errors.scheduledTime,
                                                helperText: errors.scheduledTime
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            {/* ── Scans Section ─────────────────────────── */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    {t('navigation.scans')}
                                </Typography>

                                {form.scans.map((scanItem, index) => {
                                    const scanObject = getScanObject(scanItem.scan);

                                    return (
                                        <Box
                                            key={index}
                                            sx={{
                                                border: '1px solid #e0e0e0',
                                                p: 2,
                                                mb: 2,
                                                borderRadius: 2
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    mb: scanItem.items?.length > 0 ? 2 : 0
                                                }}
                                            >
                                                <Box sx={{ flexGrow: 1 }}>
                                                    <OptimizedSearchableAutocomplete
                                                        label={`${t('navigation.scan')} #${index + 1}`}
                                                        searchFunction={searchScansWithBranch}
                                                        value={scanObject}
                                                        onChange={(e, newValue) =>
                                                            handleScanChange(
                                                                index,
                                                                'scan',
                                                                newValue
                                                            )
                                                        }
                                                        getOptionLabel={(option) =>
                                                            option?.name || ''
                                                        }
                                                        disabled={!form.branch}
                                                        initialOptions={scanInitialOptions}
                                                        getResults={getStandardResults}
                                                        getPagination={getStandardPagination}
                                                    />
                                                </Box>

                                                <TextField
                                                    label={t('common.quantity')}
                                                    type="number"
                                                    value={scanItem.quantity}
                                                    onChange={(e) =>
                                                        handleScanChange(
                                                            index,
                                                            'quantity',
                                                            Math.max(
                                                                1,
                                                                parseInt(e.target.value, 10)
                                                            ) || 1
                                                        )
                                                    }
                                                    InputProps={{
                                                        inputProps: { min: 1 }
                                                    }}
                                                    sx={{ width: '100px' }}
                                                    disabled={!form.branch || !scanItem.scan}
                                                />

                                                {form.scans.length > 1 && (
                                                    <IconButton
                                                        onClick={() => removeScan(index)}
                                                        color="error"
                                                    >
                                                        <RemoveCircleOutline />
                                                    </IconButton>
                                                )}
                                            </Box>

                                            {/* Scan consumables */}
                                            {scanItem.items?.length > 0 && (
                                                <>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            mb: 1,
                                                            mt: 1,
                                                            color: 'text.secondary',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {t('appointments.consumables')}
                                                    </Typography>
                                                    <Grid container spacing={2} sx={{ pl: 2 }}>
                                                        {scanItem.items.map(
                                                            (item, itemIndex) => (
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    sm={6}
                                                                    md={4}
                                                                    key={
                                                                        item._id || itemIndex
                                                                    }
                                                                >
                                                                    <TextField
                                                                        fullWidth
                                                                        label={item.item}
                                                                        type="number"
                                                                        value={item.quantity}
                                                                        onChange={(e) =>
                                                                            handleScanItemChange(
                                                                                index,
                                                                                itemIndex,
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        InputProps={{
                                                                            inputProps: { min: 0 }
                                                                        }}
                                                                        variant="outlined"
                                                                        size="small"
                                                                    />
                                                                </Grid>
                                                            )
                                                        )}
                                                    </Grid>
                                                </>
                                            )}
                                        </Box>
                                    );
                                })}

                                <Button
                                    onClick={addScan}
                                    startIcon={<AddIcon />}
                                    disabled={!form.branch}
                                    variant="outlined"
                                    sx={{ mt: 2 }}
                                >
                                    {t('appointments.addScan')}
                                </Button>

                                {!form.branch && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{ mt: 1 }}
                                    >
                                        {t('appointments.selectBranchFirst')}
                                    </Typography>
                                )}

                                {errors.scans && (
                                    <Typography color="error" sx={{ mt: 1 }}>
                                        {errors.scans}
                                    </Typography>
                                )}
                            </Grid>

                            {/* ── Pricing Options ───────────────────────── */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    {t('appointments.pricingOptions')}
                                </Typography>
                                <DiscountOptions
                                    basePrice={basePrice}
                                    discountAmount={form.discountAmount}
                                    onDiscountChange={handleDiscountChange}
                                    completionFee={form.completionFee}
                                    onCompletionFeeChange={handleCompletionFeeChange}
                                    customPrice={form.customPrice}
                                    onCustomPriceChange={handleCustomPriceChange}
                                    hasHugeSalePrivilege={hasHugeSalePrivilege}
                                    isSuperAdmin={isSuperAdmin}
                                    maxDiscountPercentage={isSuperAdmin ? 100 : MAX_DISCOUNT_PERCENTAGE}
                                    t={t}
                                />

                                {/* ═══════════════════════════════════════════════════════ */}
                                {/* 🔒 Discount limit indicator for non-superAdmin users  */}
                                {/* ═══════════════════════════════════════════════════════ */}
                                {!isSuperAdmin && basePrice > 0 && !form.makeHugeSale && (
                                    <Alert
                                        severity={errors.discountAmount ? 'error' : 'info'}
                                        sx={{ mt: 1 }}
                                        icon={false}
                                    >
                                        <Typography variant="caption">
                                            {t('appointments.maxDiscountNote') ||
                                                `Maximum discount allowed: ${MAX_DISCOUNT_PERCENTAGE}% (${(basePrice * MAX_DISCOUNT_PERCENTAGE / 100).toFixed(2)} EGP)`}
                                            {parsedDiscountAmount > 0 && basePrice > 0 && (
                                                <strong>
                                                    {` — Current: ${((parsedDiscountAmount / basePrice) * 100).toFixed(1)}%`}
                                                </strong>
                                            )}
                                        </Typography>
                                    </Alert>
                                )}

                                {errors.discountAmount && (
                                    <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                                        {errors.discountAmount}
                                    </Typography>
                                )}
                                {errors.makeHugeSale && (
                                    <Typography color="error" variant="caption">
                                        {errors.makeHugeSale}
                                    </Typography>
                                )}
                                {errors.customPrice && (
                                    <Typography color="error" variant="caption">
                                        {errors.customPrice}
                                    </Typography>
                                )}
                            </Grid>

                            {/* ── Price Summary ─────────────────────────── */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    {t('appointments.priceSummary')}
                                </Typography>
                                <PriceSummary
                                    basePrice={basePrice}
                                    discountAmount={parsedDiscountAmount}
                                    completionFee={parsedCompletionFee}
                                    customPrice={parsedCustomPrice}
                                    makeHugeSale={form.makeHugeSale}
                                    finalPrice={finalPrice}
                                    selectedBranch={form.branch}
                                    branches={branches}
                                    selectedScans={form.scans}
                                    scansList={[
                                        ...scans,
                                        ...Object.values(selectedScans)
                                    ]}
                                    t={t}
                                />
                            </Grid>

                            {/* ── Payment Status ────────────────────────── */}
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: form.isPaid
                                            ? 'success.main'
                                            : 'grey.300',
                                        borderRadius: 2,
                                        backgroundColor: form.isPaid
                                            ? 'success.lighter'
                                            : 'transparent',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={form.isPaid}
                                                onChange={handleChange}
                                                name="isPaid"
                                                color="success"
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography
                                                    variant="body1"
                                                    fontWeight="medium"
                                                >
                                                    {t('appointments.markAsPaid')}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Box>
                            </Grid>

                            {/* ── Notes ─────────────────────────────────── */}
                            <Grid item xs={12}>
                                <TextField
                                    label={t('common.notes')}
                                    name="notes"
                                    value={form.notes || ''}
                                    onChange={handleChange}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    placeholder={
                                        t('appointments.notesPlaceholder') ||
                                        'Add any additional notes here...'
                                    }
                                />
                            </Grid>
                        </Grid>
                    </form>
                </DialogContent>

                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={onClose} variant="outlined">
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="submit"
                        form="appointment-form"
                        variant="contained"
                        color="primary"
                    >
                        {initialData ? t('common.update') : t('common.create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Add Patient Dialog ─────────────────────────────────────── */}
            <PatientFormDialog
                open={showAddPatient}
                onClose={() => setShowAddPatient(false)}
                onSubmit={handlePatientFormSubmit}
            />

            {/* ── Confirmation Dialog ────────────────────────────────────── */}
            <ConfirmationDialog
                open={showConfirm}
                onClose={() => setShowConfirm(false)}
                onConfirm={handleConfirm}
                pendingSubmission={pendingSubmission}
                scansList={[...scans, ...Object.values(selectedScans)]}
                t={t}
            />
        </>
    );
}

export default AppointmentForm;